import {
  useState,
  useCallback,
  ReactNode,
  useRef,
  useEffect,
  MouseEvent,
  KeyboardEvent,
  Fragment,
  ComponentType,
  SVGProps,
} from 'react';
import { createPortal } from 'react-dom';
import { cn, Tooltip } from '@c2fo/liquidity';
import { CaretLeftSolidIcon } from '@c2fo/liquidity/icons';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import ChatFooter from './ChatFooter';
import { defaultRenderItem, defaultRenderSubItem } from './defaultRenderers';
import NavigationHeader, { NavigationHeaderProps } from './NavigationHeader';

export interface BaseNavigationItem {
  id: string;
  label: string | ReactNode;
  Icon?: ComponentType<SVGProps<SVGSVGElement>>;
  disabled?: boolean;
  external?: boolean;
  metadata?: Record<string, string | number | boolean>;
  intercomTarget?: IntercomDataTargetNames;
}

export interface NavigationItem extends BaseNavigationItem {
  path?: string;
  externalRedirectKey?: string;
  subItems?: NavigationItem[];
  data?: Record<string, string | number | boolean>;
  additionalAttributes?: Record<string, string>;
  position?: 'top' | 'bottom';
  isDivider?: boolean;
  endSlot?: ReactNode;
  breadcrumbLabel?: string;
}

export interface RenderItemProps {
  item: NavigationItem;
  isActive: boolean;
  isDisabled: boolean;
  hasSubItems: boolean;
  isCollapsed?: boolean;
  isSubNavOpen?: boolean;
  onClick: (event: MouseEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
}

export interface RenderSubItemProps {
  item: NavigationItem;
  parentItem: NavigationItem;
  isActive: boolean;
  isDisabled: boolean;
  onClick: (event: MouseEvent) => void;
  onKeyDown: (event: KeyboardEvent) => void;
}

export interface NavigationChatConfig {
  chatText: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  animationClassName?: string;
}

export interface NavigationHeaderConfig {
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  as?: React.ElementType;
}

export interface NavigationProps {
  items: NavigationItem[];
  initialActiveItemId?: string;
  activeItemId?: string;
  isCollapsed?: boolean;
  isAutoWidth?: boolean;
  header?: NavigationHeaderConfig | boolean;
  chat?: NavigationChatConfig;
  onItemChange?: (activeItem: NavigationItem, parentItem?: NavigationItem) => void;
  onExternalRedirect?: (redirectKey: string) => void;
  onToggleCollapsed?: () => void;
  renderItem?: (props: RenderItemProps) => ReactNode;
  renderSubItem?: (props: RenderSubItemProps) => ReactNode;
  portalContainer?: Element | null;
  shouldUsePortal?: boolean;
  variant?: 'desktop' | 'mobile';
  externalIsSubNavOpen?: boolean;
  externalSelectedItemId?: string;
  onSubNavOpenChange?: (isOpen: boolean) => void;
  onSelectedItemChange?: (itemId: string | undefined) => void;
}

export const Navigation = ({
  items,
  initialActiveItemId,
  activeItemId: externalActiveItemId,
  isCollapsed = false,
  isAutoWidth = false,
  header,
  chat,
  onItemChange,
  onExternalRedirect,
  onToggleCollapsed,
  renderItem = defaultRenderItem,
  renderSubItem = defaultRenderSubItem,
  portalContainer,
  shouldUsePortal = false,
  variant = 'desktop',
  externalIsSubNavOpen,
  externalSelectedItemId,
  onSubNavOpenChange,
  onSelectedItemChange,
}: NavigationProps) => {
  const [activeItemId, setActiveItemId] = useState<string | undefined>(initialActiveItemId);
  const [internalSelectedItemId, setInternalSelectedItemId] = useState<string | undefined>();
  const [internalIsSubNavOpen, setInternalIsSubNavOpen] = useState(false);
  const [disableSubNavAnimation, setDisableSubNavAnimation] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const selectedItemId = externalSelectedItemId ?? internalSelectedItemId;
  const isSubNavOpen = externalIsSubNavOpen ?? internalIsSubNavOpen;

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const setSelectedItemId = useCallback(
    (id: string | undefined) => {
      if (onSelectedItemChange) {
        onSelectedItemChange(id);
      } else {
        setInternalSelectedItemId(id);
      }
    },
    [onSelectedItemChange]
  );

  const setIsSubNavOpen = useCallback(
    (isOpen: boolean) => {
      if (onSubNavOpenChange) {
        onSubNavOpenChange(isOpen);
      } else {
        setInternalIsSubNavOpen(isOpen);
      }
    },
    [onSubNavOpenChange]
  );

  // sync with external activeItemId
  useEffect(() => {
    if (externalActiveItemId !== undefined && externalActiveItemId !== activeItemId) {
      setActiveItemId(externalActiveItemId);
    }
  }, [externalActiveItemId, activeItemId]);

  const handleItemClick = useCallback(
    (item: NavigationItem) => {
      if (item.disabled) return;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      // handle external redirects
      if (item.externalRedirectKey) {
        onExternalRedirect?.(item.externalRedirectKey);
        return;
      }

      // if item has sub-items, handle based on collapsed state
      if (item.subItems?.length) {
        // find the first non-disabled sub-item to navigate to
        const firstEnabledSubItem = item.subItems.find((subItem) => !subItem.disabled && subItem.path);

        if (isCollapsed && onToggleCollapsed) {
          // when collapsed, expand nav and show sub-nav
          onToggleCollapsed();
          setSelectedItemId(item.id);
          setDisableSubNavAnimation(true); // disable animation to show sub-nav immediately
          setIsSubNavOpen(true);

          // re-enable animation after expand completes
          timeoutRef.current = setTimeout(() => {
            setDisableSubNavAnimation(false);
          }, 250);
        } else {
          // when expanded, open sub-nav immediately with animation
          setDisableSubNavAnimation(false);
          setSelectedItemId(item.id);
          setIsSubNavOpen(true);
        }

        // navigate to the first enabled sub-item if one exists
        if (firstEnabledSubItem) {
          setActiveItemId(firstEnabledSubItem.id);
          onItemChange?.(firstEnabledSubItem, item);
        }

        return;
      }

      // regular item click - set as active and close sub-nav
      setActiveItemId(item.id);
      setSelectedItemId(undefined);
      setIsSubNavOpen(false);
      onItemChange?.(item);
    },
    [onExternalRedirect, onItemChange, onToggleCollapsed, isCollapsed, setIsSubNavOpen, setSelectedItemId]
  );

  const handleSubItemClick = useCallback(
    (subItem: NavigationItem, parentItem: NavigationItem) => {
      if (subItem.disabled) return;

      // set sub-item as active but keep sub-nav open
      setActiveItemId(subItem.id);
      // Keep selectedItemId as the parent to maintain sub-nav open state
      // setSelectedItemId(undefined);
      // setIsSubNavOpen(false);
      onItemChange?.(subItem, parentItem);
    },
    [onItemChange]
  );

  const handleSubNavClose = useCallback(() => {
    setIsSubNavOpen(false);
    setSelectedItemId(undefined);
    setDisableSubNavAnimation(false);
  }, [setIsSubNavOpen, setSelectedItemId]);

  const handleDOMItemClick = useCallback(
    (item: NavigationItem) => (_event: MouseEvent) => {
      if (item.disabled) return;
      handleItemClick(item);
    },
    [handleItemClick]
  );

  const handleDOMItemKeyDown = useCallback(
    (item: NavigationItem) => (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        handleItemClick(item);
      }
    },
    [handleItemClick]
  );

  const handleDOMSubItemClick = useCallback(
    (subItem: NavigationItem, parentItem: NavigationItem) => (_event: MouseEvent) => {
      handleSubItemClick(subItem, parentItem);
    },
    [handleSubItemClick]
  );

  const handleKeyDown = useCallback((event: KeyboardEvent, callback: () => void) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      callback();
    }
  }, []);

  const selectedPrimaryNavItem = items.find((item) => item.id === selectedItemId);
  const fullWidth = isAutoWidth ? 'w-full' : 'w-64';
  const width = isCollapsed ? 'w-14' : fullWidth;
  const headerConfig = typeof header === 'object' ? header : {};
  const shouldRenderChat = typeof chat === 'object' && chat !== null;

  // separate items by position
  const topItems = items.filter((item) => item.position !== 'bottom');
  const bottomItems = items.filter((item) => item.position === 'bottom');

  // create separate element instances for portal vs non-portal
  const renderSubNavContent = () => {
    if (!selectedPrimaryNavItem) {
      return null;
    }

    return (
      <div className="flex h-full w-full flex-col gap-4 p-3 pt-8">
        <div
          className="flex items-center gap-0 pr-2"
          role="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubNavClose();
          }}
          onKeyDown={(e) => handleKeyDown(e, () => handleSubNavClose())}
          tabIndex={isSubNavOpen ? 0 : -1}
        >
          <CaretLeftSolidIcon className="opacity-25" />
          <p className="text-sm font-semibold text-text-secondary">{selectedPrimaryNavItem.label}</p>
        </div>
        <div className="overflow-y-auto">
          {selectedPrimaryNavItem.subItems?.map((subItem) => {
            const isActive = activeItemId === subItem.id;
            const isDisabled = subItem.disabled || !subItem.path;

            return (
              <Fragment key={subItem.id}>
                {renderSubItem({
                  item: subItem,
                  parentItem: selectedPrimaryNavItem,
                  isActive,
                  isDisabled,
                  onClick: handleDOMSubItemClick(subItem, selectedPrimaryNavItem),
                  onKeyDown: (e) => handleKeyDown(e, () => handleSubItemClick(subItem, selectedPrimaryNavItem)),
                })}
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Tooltip.Provider>
      <IntercomDataTarget
        className={cn('h-full overflow-hidden bg-white transition-[width] duration-200 ease-in-out', width, {
          'border-r': variant === 'desktop',
        })}
        target="sideNavArea"
      >
        <div className={cn('relative h-full overflow-hidden', fullWidth)}>
          <div className="flex h-full flex-col gap-6 pt-2 md:pt-6">
            <NavigationHeader
              isCollapsed={isCollapsed}
              isSubNavOpen={isSubNavOpen}
              {...(headerConfig as Partial<NavigationHeaderProps>)}
            />
            <div className="flex flex-1 flex-col">
              <div className="flex-1">
                {topItems.map((item) => {
                  const hasSubItems = !!item.subItems?.length;

                  // check if the active item is a sub-item of this parent
                  const activeItemIsSubOfThis =
                    hasSubItems && (item.subItems?.some((subItem) => subItem.id === activeItemId) || false);

                  // handle active state
                  let isActive = false;

                  if (isSubNavOpen) {
                    if (isCollapsed) {
                      // when collapsed and sub-nav is open:
                      // 1. if active item is a sub-item of this parent, highlight this parent
                      // 2. otherwise, fallback to highlighting the currently active item
                      isActive = activeItemIsSubOfThis || activeItemId === item.id;
                    } else {
                      // when expanded and sub-nav is open, highlight the selected primary item (parent of open sub-nav)
                      isActive = selectedPrimaryNavItem?.id === item.id;
                    }
                  } else {
                    // when sub-nav is closed, highlight:
                    // 1. the direct active item, OR
                    // 2. parent items whose sub-item is currently active
                    isActive = activeItemId === item.id || activeItemIsSubOfThis;
                  }

                  const isDisabled =
                    item.disabled || (!item.path && !item.externalRedirectKey && !item.subItems?.length);

                  // render divider items as dividers
                  if (item.isDivider) {
                    return <div key={item.id} className="my-2 border-t border-dashed" />;
                  }

                  return (
                    <div key={item.id}>
                      {renderItem({
                        item,
                        isActive,
                        isDisabled,
                        hasSubItems,
                        isCollapsed,
                        isSubNavOpen,
                        onClick: handleDOMItemClick(item),
                        onKeyDown: handleDOMItemKeyDown(item),
                      })}
                    </div>
                  );
                })}
              </div>
              {bottomItems.length > 0 && (
                <div className="pt-4">
                  {bottomItems.map((item) => {
                    const hasSubItems = !!item.subItems?.length;

                    // check if the active item is a sub-item of this parent
                    const activeItemIsSubOfThis =
                      hasSubItems && (item.subItems?.some((subItem) => subItem.id === activeItemId) || false);

                    // handle active state
                    let isActive = false;

                    if (isSubNavOpen) {
                      if (isCollapsed) {
                        // when collapsed and sub-nav is open:
                        // 1. if active item is a sub-item of this parent, highlight this parent
                        // 2. otherwise, fallback to highlighting the currently active item
                        isActive = activeItemIsSubOfThis || activeItemId === item.id;
                      } else {
                        // when expanded and sub-nav is open, highlight the selected primary item (parent of open sub-nav)
                        isActive = selectedPrimaryNavItem?.id === item.id;
                      }
                    } else {
                      // when sub-nav is closed, highlight:
                      // 1. the direct active item, OR
                      // 2. parent items whose sub-item is currently active
                      isActive = activeItemId === item.id || activeItemIsSubOfThis;
                    }

                    const isDisabled =
                      item.disabled || (!item.path && !item.externalRedirectKey && !item.subItems?.length);

                    return (
                      <div key={item.id}>
                        {renderItem({
                          item,
                          isActive,
                          isDisabled,
                          hasSubItems,
                          isCollapsed,
                          isSubNavOpen,
                          onClick: handleDOMItemClick(item),
                          onKeyDown: handleDOMItemKeyDown(item),
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
              {shouldRenderChat && (
                <div className="mt-2 border-t border-dashed">
                  <ChatFooter isCollapsed={isCollapsed} isSubNavOpen={isSubNavOpen} {...chat} />
                </div>
              )}
            </div>
          </div>
          {/* sub-nav container */}
          {!shouldUsePortal && (
            <div
              className={cn('absolute right-0 top-0 z-20 h-full transform bg-deepBlue-50', {
                'w-full': variant === 'mobile',
                'w-[calc(100%-56px)]': variant === 'desktop',
                'transition-transform duration-200 ease-in-out': !disableSubNavAnimation,
                'translate-x-0': isSubNavOpen,
                'translate-x-full': !isSubNavOpen,
              })}
              {...(selectedPrimaryNavItem?.id === 'invoice-central' && {
                'data-intercom-target': 'invoiceCentralSubNav' satisfies IntercomDataTargetNames,
              })}
            >
              {renderSubNavContent()}
            </div>
          )}
        </div>
      </IntercomDataTarget>
      {/* portaled sub-nav container */}
      {shouldUsePortal &&
        portalContainer &&
        createPortal(
          <div
            className={cn('absolute inset-0 z-10 overflow-hidden', {
              'pointer-events-none': !isSubNavOpen,
            })}
          >
            <div
              className={cn('absolute left-0 top-0 h-full w-full transform bg-deepBlue-50', {
                'transition-transform duration-200 ease-in-out': !disableSubNavAnimation,
                'translate-x-0': isSubNavOpen,
                'translate-x-full': !isSubNavOpen,
              })}
            >
              {renderSubNavContent()}
            </div>
          </div>,
          portalContainer
        )}
    </Tooltip.Provider>
  );
};
